import React, { useState, useEffect, useRef, useCallback } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { downloadFile, getFileLogsByTenant } from "../../services/tenant/tenant";
import { useSelector } from "react-redux";
import { toast } from "../../components/AppToast";
import { formatToDisplayDate } from "../../utils/dateFormatter";

// Tools page — matches AdminSettings/TenantCard card style
export default function Tools({ onClose }) {
  const { userDetail } = useSelector((state) => state.login);
  const tenantId = userDetail?.tenantId?._id || userDetail?.tenantId;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const fileInputRef = useRef(null);

  // Fetch file logs
  const fetchLogs = useCallback(async () => {
    if (!tenantId) {
      toast.error("Tenant ID not found");
      return;
    }

    setLoading(true);
    try {
      const response = await getFileLogsByTenant(tenantId);
      
      // Handle response structure (adapt to your API shape)
      const data = Array.isArray(response) ? response : response?.data || response?.items || [];
      const total = response?.totalCount ?? response?.meta?.total ?? (Array.isArray(data) ? data.length : 0);

      // Format rows
      const formattedRows = data.map((log, idx) => {
        const isExportSuccess =
          log.type === "export" && log.status === "success";

        return {
          id: log._id || idx,
          _id: log._id,
          fileName: log.fileName || "-",
          module: log.module || "-",
          type: log.type
            ? log.type.charAt(0).toUpperCase() + log.type.slice(1)
            : "-",

          status: log.displayStatus || log.status || "-",

          totalRows: log.totalRows ?? "-",

          // 🔥 MAIN CHANGE
          successRows: isExportSuccess ? "-" : log.successRows ?? 0,
          failedRows: isExportSuccess ? "-" : log.failedRows ?? 0,

          failedFileUrl: log.failedFileUrl || null,
          downloadUrl: log.downloadUrl || null,

          createdAt: log.timestamp
            ? formatToDisplayDate(log.timestamp, true, "24h")
            : "-",

          createdBy: log.userId?.email || "-",
          raw: log,
        };
      });


      setLogs(formattedRows);
      setTotalCount(total);
    } catch (err) {
      console.error("Failed to fetch logs:", err);
      toast.error(err?.message || "Failed to load file logs");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Load logs on mount and page change
  useEffect(() => {
    fetchLogs(page, pageSize);
  }, [fetchLogs, page, pageSize]);

  // Download failed file
  const handleDownloadFailed = async (row) => {

    const resp = await downloadFile(row._id);

    if (resp.downloadUrl) {
      window.open(resp.downloadUrl, "_blank");
    } else {
      toast.info("No failed file available");
    }

  };

  // Table columns
  const columns = [
    {
      field: "fileName",
      headerName: "File Name",
      width: 200,
      sortable: true,
    },
    {
      field: "module",
      headerName: "Module",
      width: 150,
      sortable: true,
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const status = (params.value || "pending").toString().toLowerCase();
        const badgeClass = status === "success" ? "bg-success" :
                          status === "failed" ? "bg-danger" :
                          "bg-warning";
        return <span className={`badge ${badgeClass}`}>{params.value}</span>;
      },
    },
    {
      field: "totalRows",
      headerName: "Total Rows",
      width: 120,
      type: "number",
    },
    {
      field: "successRows",
      headerName: "Success Rows",
      width: 120,
      type: "number",
    },
     {
      field: "failedRows",
      headerName: "Failed Rows",
      width: 120,
      type: "number",
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 180,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 150,
    },
    //  {
    //   field: "progressPercentage",
    //   headerName: "Progress Percentage",
    //   width: 150,
    // },
    {
      field: "actions",
      headerName: "Logs",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = (params.row.status || "").toLowerCase();
        const type = (params.row.type || "").toLowerCase();

        // ✅ HIDE download button when status=success AND type=import
        if (status === "success" && type === "import") {
          return <span className="text-muted"></span>;
        }

        // File available check
        const hasFile =
          params.row.failedFileUrl ||
          params.row.downloadUrl ||
          params.row.raw?.fileUrl;

        return (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleDownloadFailed(params.row)}
            disabled={!hasFile}
            title="Download failed records"
          >
            <i className="bi bi-download me-1"></i>
            Download
          </button>
        );
      },
    }


  ];


  const handleManualRefresh = async () => {
    await fetchLogs(page, pageSize);
  };


  return (
    <div className="card shadow-lg">
      <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0 fw-semibold">Tools - File Management</h5>
        {onClose && (
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={onClose}
            aria-label="Close"
          ></button>
        )}
      </div>

      <div className="card-body">
        {/* File Logs Table */}
     

        {loading && <div className="text-center py-4 text-muted">Loading...</div>}

        {!loading && (
          <CommonDataTable
            id="tools-file-logs"
            allPossibleColumns={columns}
            initialVisible={columns.map((c) => c.field)}
            initialRows={logs}
            height={400}
            rowsPerPageOptions={[5 ,10, 25, 50]}
            page={page}
            pageSize={pageSize}
            rowCount={totalCount}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            checkboxSelection={false}


            enableImportButton={false}   // ⛔ Hide Import Button
            enableExportButton={false}  // ⛔ Hide Export Button

            forInventory={true}               // 🔄 shows refresh button
            onManualRefresh={handleManualRefresh}
            
          />
        )}
      </div>
    </div>
  );
}



// <h6 className="fw-bold mb-3">
//   <i className="bi bi-file-earmark-text me-2"></i>Upload
// </h6>